import * as React from 'react';
import { type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { useArtifacts } from '@/api/queries/useArtifacts';
import { distinctValues, FormCreatableCombobox } from './FormCreatableCombobox';

export interface FormArtifactTypeSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  /**
   * React Hook Form control object
   */
  control: Control<TFieldValues>;

  /**
   * Field name in the form
   */
  name: TName;

  /**
   * Field label
   */
  label?: string;

  /**
   * Placeholder text
   */
  placeholder?: string;
}

/**
 * React Hook Form adapter for the artifact type field.
 * Automatically fetches artifacts and suggests the distinct types already
 * in use, while still allowing a brand-new value via "Create" — the same
 * search-or-create pattern as the system category field.
 */
export function FormArtifactTypeSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: FormArtifactTypeSelectProps<TFieldValues, TName>) {
  const { data: artifacts, isLoading } = useArtifacts();

  const types = React.useMemo(
    () => distinctValues(artifacts?.map((artifact) => artifact.type)),
    [artifacts]
  );

  return <FormCreatableCombobox {...props} options={types} isLoading={isLoading} />;
}

FormArtifactTypeSelect.displayName = 'FormArtifactTypeSelect';
